import { useEffect } from 'react';

import {
  getAccountTypeForConnection,
  isChallengeAccountType,
  productId,
} from '@trader/constants';
import { useMst } from '@trader/store';

import { EConnectionHub, EConnectionHubSubscriber } from './core/types';
import { useGetPageVisibility } from '../useGetPageVisibility';
import { useHub } from './core/useHub';

interface IMessage {
  marginFree: number;
  freeMargin: number;
  balance: number;
  equity: number;
  marginLevel: number;
  profit: number;
  credit: number;
  brand: number;
  login: string;
  pl: number;
  currentLogin: number;
}

export const usePortfolio = (
  platformLogin?: string,
  accType?: string,
  onUpdate?: (message: IMessage) => void
) => {
  const store = useMst();
  const isVisible = useGetPageVisibility();

  const idToken = store.auth.tokens.idToken || '';

  const handleUpdate = (message: IMessage) => {
    if (onUpdate) {
      onUpdate({
        ...message,
        freeMargin: message.marginFree,
        pl: message.profit,
        currentLogin: Number(login),
      });
    } else {
      const portfolio = {
        equity: message.equity,
        balance: message.balance,
        pl: message.profit,
        freeMargin: message.marginFree,
        usedMargin: message.equity - message.marginFree,
        marginLevel: message.marginLevel,
        credit: message.credit,
      };
      store.user.updatePortfolioFromSockets(portfolio, message.login);
      isChallengeAccountType && store.user.updateChallengeFromSockets(message);
    }
  };

  const activeTradingAccount = store.user.activeTradingAcc();
  const login = platformLogin || activeTradingAccount?.platformLogin || '';
  const accountType = accType || activeTradingAccount?.accountType || '';

  const { unsubscribe, subscribe } = useHub<IMessage>({
    url: `${import.meta.env.VITE_SIGNALR_URL}/v2/account`,
    hub: EConnectionHub.Account,
    subscriber: EConnectionHubSubscriber.Portfolio,
    onMessage: handleUpdate,
    invokedName: 'portfolio',
    subscriptionArgs: {
      login: login,
      productId: productId[import.meta.env.VITE_PRODUCT],
      accountType: getAccountTypeForConnection[accountType],
    },
  });

  useEffect(() => {
    if (login && accountType) {
      subscribe(async connection => {
        await connection?.send(
          'SubscribeOnPortfolio',
          login || null,
          productId[import.meta.env.VITE_PRODUCT],
          getAccountTypeForConnection[accountType]
        );
      });
    }
    return () => {
      if (login && accountType) {
        Number(activeTradingAccount?.platformLogin) !== Number(platformLogin) &&
          unsubscribe(async connection => {
            await connection?.send(
              'UnsubscribeFromPortfolio',
              login || null,
              productId[import.meta.env.VITE_PRODUCT],
              getAccountTypeForConnection[accountType]
            );
          });
      }
    };
  }, [login, idToken]);

  useEffect(() => {
    if (isVisible) {
      subscribe(async connection => {
        await connection?.send(
          'SubscribeOnPortfolio',
          login || null,
          productId[import.meta.env.VITE_PRODUCT],
          getAccountTypeForConnection[accountType]
        );
      });
    } else {
      unsubscribe(async connection => {
        await connection?.send(
          'UnsubscribeFromPortfolio',
          login || null,
          productId[import.meta.env.VITE_PRODUCT],
          getAccountTypeForConnection[accountType]
        );
      });
    }
  }, [isVisible]);
};
